import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, Loader2, ShieldAlert } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Deployment, UpdateDeploymentRequest } from '../../types/strategyLab';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface AdjustDeploymentDialogProps {
  deployment: Deployment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * On-the-fly deployment adjustment — budget + daily-loss stop. Both edits are
 * reduce-only-safe on the backend; here we only guard obvious nonsense
 * (positive values, loss <= budget) and let the API enforce the rest.
 */
const AdjustDeploymentDialog: React.FC<AdjustDeploymentDialogProps> = ({
  deployment, open, onOpenChange,
}) => {
  const updateDeployment = useStrategyLabStore((s) => s.updateDeployment);
  const actionError = useStrategyLabStore((s) => s.actionError);
  const clearActionError = useStrategyLabStore((s) => s.clearActionError);

  const [budget, setBudget] = useState(0);
  const [maxLoss, setMaxLoss] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && deployment) {
      setBudget(deployment.budget_usd);
      setMaxLoss(deployment.max_daily_loss_usd ?? Math.round(deployment.budget_usd * 0.02));
      setSaving(false);
      clearActionError();
    }
  }, [open, deployment, clearActionError]);

  const valid = budget > 0 && maxLoss > 0 && maxLoss <= budget;

  const handleSave = async () => {
    if (!deployment || !valid) return;
    setSaving(true);
    const body: UpdateDeploymentRequest = {
      budget_usd: budget,
      max_daily_loss_usd: maxLoss,
    };
    const result = await updateDeployment(deployment.id, body);
    setSaving(false);
    if (result) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
            Adjust deployment
          </DialogTitle>
          <DialogDescription>
            {deployment?.strategy_name
              ? <>Tune <span className="font-medium text-foreground">{deployment.strategy_name}</span> on the fly.</>
              : 'Tune the live budget and daily-loss stop.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Daily budget ($)</Label>
            <Input
              type="number" min={0} step={500} value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max daily loss ($)</Label>
            <Input
              type="number" min={0} step={50} value={maxLoss}
              onChange={(e) => setMaxLoss(Number(e.target.value))}
            />
          </div>

          {maxLoss > budget && (
            <p className="text-sm text-rose-500 flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" /> Loss stop can't exceed the budget.
            </p>
          )}
          {actionError && <p className="text-sm text-rose-500">{actionError}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!valid || saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <SlidersHorizontal className="w-4 h-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustDeploymentDialog;
